<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <v-alert v-if="flash" type="success" density="compact" closable class="mb-4" :text="flash" @click:close="flash = ''" />
          <v-alert v-if="errorMessage" type="error" density="compact" closable class="mb-4" :text="errorMessage" @click:close="errorMessage = ''" />

          <!-- 管理者: 承認待ちのサマリ -->
          <v-card v-if="admin" variant="tonal" color="amber-darken-3" class="mb-6">
            <v-card-text class="d-flex align-center">
              <v-icon size="32" class="mr-4">mdi-check-decagram-outline</v-icon>
              <div class="flex-grow-1">
                <div class="text-subtitle-1 font-weight-bold">{{ $t('labelOrder.label.adminNoticeTitle') }}</div>
                <div class="text-body-2">{{ $t('labelOrder.label.adminNoticeText') }}</div>
              </div>
              <v-sheet class="px-5 py-2 mx-2 text-center rounded" color="white">
                <div class="text-caption text-medium-emphasis">{{ $t('labelOrder.label.pendingCount') }}</div>
                <div class="text-h6 text-orange-darken-3">{{ pending.count }} {{ $t('labelOrder.label.unitCount') }}</div>
              </v-sheet>
              <v-sheet class="px-5 py-2 mx-2 text-center rounded" color="white">
                <div class="text-caption text-medium-emphasis">{{ $t('labelOrder.label.pendingQty') }}</div>
                <div class="text-h6 text-orange-darken-3">{{ num(pending.qty) }}</div>
              </v-sheet>
            </v-card-text>
          </v-card>

          <!-- 申請一覧 -->
          <v-row align="end" class="mb-2">
            <v-col>
              <div class="text-subtitle-1 font-weight-bold">{{ $t('labelOrder.label.list') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ admin ? $t('labelOrder.label.listNoteAdmin') : $t('labelOrder.label.listNoteUser') }}
              </div>
            </v-col>
            <v-col cols="auto">
              <v-btn class="px-6 bg-indigo-darken-1" elevation="2" @click="openNew">{{ $t('labelOrder.action.new') }}</v-btn>
            </v-col>
          </v-row>

          <v-table density="compact" class="text-no-wrap">
            <thead>
              <tr>
                <th>{{ $t('labelOrder.label.table.requestId') }}</th>
                <th>{{ $t('labelOrder.label.table.requestDate') }}</th>
                <th>{{ $t('labelOrder.label.table.requestUser') }}</th>
                <th class="text-right">{{ $t('labelOrder.label.table.lineCount') }}</th>
                <th class="text-right">{{ $t('labelOrder.label.table.requestedTotal') }}</th>
                <th class="text-right">{{ $t('labelOrder.label.table.approvedTotal') }}</th>
                <th>{{ $t('labelOrder.label.table.deliveryDate') }}</th>
                <th>{{ $t('labelOrder.label.table.status') }}</th>
                <th class="text-right">{{ admin ? $t('labelOrder.label.table.action') : '' }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in requestList" :key="r.requestId" style="cursor: pointer;" @click="openDetail(r)">
                <td class="font-weight-bold text-primary">{{ r.requestId }}</td>
                <td>{{ r.requestDate }}</td>
                <td>{{ r.requestUserName || r.requestUserId }}</td>
                <td class="text-right">{{ r.lineCount }} {{ $t('labelOrder.label.unitStore') }}</td>
                <td class="text-right">{{ num(r.requestedTotal) }}</td>
                <td class="text-right">
                  <template v-if="r.approvedTotal != null">
                    <span class="font-weight-bold">{{ num(r.approvedTotal) }}</span>
                    <span v-if="r.changedCount > 0" class="text-caption text-amber-darken-3 font-weight-bold ml-1">
                      {{ $t('labelOrder.label.changedSuffix', [r.changedCount]) }}
                    </span>
                  </template>
                  <span v-else class="text-disabled">—</span>
                </td>
                <td>
                  <span v-if="r.deliveryDate">{{ r.deliveryDate }}</span>
                  <span v-else class="text-disabled">—</span>
                </td>
                <td>
                  <v-chip size="small" label variant="tonal" :color="statusColor(r.status)">
                    {{ $t('labelOrder.status.' + r.status) }}
                  </v-chip>
                </td>
                <td class="text-right">
                  <template v-if="admin && r.status === STATUS.REQUESTED">
                    <v-btn size="small" class="bg-green-darken-1 mr-2" elevation="1" @click.stop="openApprove(r)">
                      {{ $t('labelOrder.action.approve') }}
                    </v-btn>
                    <v-btn size="small" variant="outlined" @click.stop="openDetail(r)">
                      {{ $t('labelOrder.action.reviseOrReject') }}
                    </v-btn>
                  </template>
                  <span v-else-if="!admin && r.status === STATUS.REQUESTED" class="text-caption text-medium-emphasis">
                    {{ $t('labelOrder.label.waitingApproval') }}
                  </span>
                  <span v-else class="text-disabled">—</span>
                </td>
              </tr>
              <tr v-if="!isLoading && requestList.length === 0">
                <td colspan="9" class="text-center text-medium-emphasis">
                  {{ $t('common.label.all') }} 0 {{ $t('common.label.totalCountOf') }}
                </td>
              </tr>
            </tbody>
          </v-table>
          <v-progress-linear v-if="isLoading" indeterminate color="indigo" />
        </v-container>

        <LabelApproveDialog
          v-model="approveDialog"
          :request-id="target?.requestId"
          :edited="false"
          :approved-total="target?.requestedTotal ?? 0"
          :line-count="target?.lineCount ?? 0"
          @approved="onApproved"
        />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import AppWrapperView from "@/components/views/AppWrapperView";
import LabelApproveDialog from "@/components/label/LabelApproveDialog";
import api from "@/apis/staff";
import router from "@/router/index";
import Paths from "@/router/indexPaths";
import { ORDER_STATUS, statusColor, num, isAdmin, popFlash } from "@/utils/labelManagement";

export default {
  components: { AppWrapperView, LabelApproveDialog },
  data: () => ({
    STATUS: ORDER_STATUS,
    admin: false,
    requestList: [],
    isLoading: false,
    flash: "",
    errorMessage: "",
    approveDialog: false,
    target: null,
  }),
  computed: {
    pending() {
      const list = this.requestList.filter((r) => r.status === ORDER_STATUS.REQUESTED);
      return { count: list.length, qty: list.reduce((a, r) => a + (r.requestedTotal ?? 0), 0) };
    },
  },
  mounted() {
    this.admin = isAdmin();
    this.flash = popFlash();
    this.load();
  },
  methods: {
    num,
    statusColor,
    load() {
      this.isLoading = true;
      api.post("/label-order/search", {})
        .then((response) => {
          if (response.data?.status == 1) {
            this.errorMessage = response.data.errorMessage;
            this.requestList = [];
            return;
          }
          this.requestList = response.data.results?.requestList ?? [];
        })
        .catch(() => {
          this.requestList = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    openNew() {
      router.push({ name: Paths.LABEL_ORDER_NEW });
    },
    openDetail(r) {
      router.push({ name: Paths.LABEL_ORDER_DETAIL, query: { requestId: r.requestId } });
    },
    openApprove(r) {
      this.target = r;
      this.approveDialog = true;
    },
    onApproved(deliveryDate) {
      this.flash = this.$t("labelOrder.message.approved", [this.target.requestId, deliveryDate]);
      this.load();
    },
  },
};
</script>
